/**
 * tee-upload-progress-monitor.js
 * TeeInBlue Upload Progress Monitor
 * Note: Translations are passed via window.teeUploadMessages config
 */

(function() {
  'use strict';

  var DEBUG = false;
  var CHECK_DEBOUNCE = 50;
  var MESSAGE_TRANSITION = 200;

  var DEFAULT_MESSAGES = [
    { threshold: 0, text: 'Preparing your design...', icon: '\uD83D\uDCCB' },
    { threshold: 25, text: 'Uploading your photo...', icon: '\uD83D\uDCE4' },
    { threshold: 50, text: 'Processing...', icon: '\u2699\uFE0F' },
    { threshold: 75, text: 'Final steps...', icon: '\u2728' },
    { threshold: 90, text: 'Almost done...', icon: '\uD83D\uDE80' }
  ];

  function getMessages() {
    return window.teeUploadMessages || DEFAULT_MESSAGES;
  }

  function shouldInitialize() {
    return document.querySelector('[id^="tee-"], .tee-field, [class*="teeinblue"]') !== null;
  }

  function log() {
    if (DEBUG) console.log.apply(console, ['[TeeInBlue Monitor]'].concat(Array.prototype.slice.call(arguments)));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      if (shouldInitialize()) initUploadMonitor();
    });
  } else if (shouldInitialize()) {
    initUploadMonitor();
  }

  function initUploadMonitor() {
    log('Initializing upload monitor...');

    var activeUploads = new Map();

    function handleUploadProgress(uploadContainer) {
      if (activeUploads.has(uploadContainer)) return;

      log('New upload detected, setting up monitor...');

      var targetContainer = uploadContainer.closest('.tee-photo-label') || uploadContainer;

      if (window.getComputedStyle(targetContainer).position === 'static') {
        targetContainer.style.position = 'relative';
      }

      var overlay = createOverlay();
      targetContainer.appendChild(overlay.element);

      var originalProgress = uploadContainer.querySelector('.tee-progress');
      if (originalProgress) originalProgress.style.visibility = 'hidden';

      var tracker = {
        container: uploadContainer,
        overlay: overlay,
        currentMessageIndex: -1,
        rafId: null,
        pendingProgress: null
      };

      activeUploads.set(uploadContainer, tracker);

      var observer = new MutationObserver(function() {
        var progressBar = uploadContainer.querySelector('.tee-progress-bar');
        var progressElement = uploadContainer.querySelector('.tee-progress');

        var isComplete =
          (progressElement && !progressElement.classList.contains('tee-progress--loading')) ||
          !uploadContainer.classList.contains('tee-photo-uploading') ||
          targetContainer.querySelector('.tee-photo-uploaded') !== null ||
          targetContainer.querySelector('.tee-photo-uploaded .tee-photo-image') !== null ||
          !document.body.contains(uploadContainer);

        if (isComplete) {
          log('Upload complete, cleaning up...');
          cleanup(uploadContainer);
          return;
        }

        if (progressBar) {
          var progress = parseInt(progressBar.getAttribute('aria-valuenow')) || 0;
          updateProgress(uploadContainer, progress);
        }
      });

      observer.observe(targetContainer, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true
      });

      tracker.observer = observer;

      var initialBar = uploadContainer.querySelector('.tee-progress-bar');
      if (initialBar) {
        var initialProgress = parseInt(initialBar.getAttribute('aria-valuenow')) || 0;
        updateProgress(uploadContainer, initialProgress);
      }
    }

    function createOverlay() {
      var overlay = document.createElement('div');
      overlay.className = 'custom-upload-overlay';

      var messageContainer = document.createElement('div');
      messageContainer.className = 'upload-message-container';

      var icon = document.createElement('span');
      icon.className = 'upload-icon';

      var text = document.createElement('span');
      text.className = 'upload-text';

      var dots = document.createElement('span');
      dots.className = 'progress-dots';
      dots.innerHTML = '<span>.</span><span>.</span><span>.</span>';

      messageContainer.appendChild(icon);
      messageContainer.appendChild(text);
      messageContainer.appendChild(dots);

      var progressBar = document.createElement('div');
      progressBar.className = 'custom-progress-bar';
      progressBar.style.width = '0%';

      overlay.appendChild(messageContainer);
      overlay.appendChild(progressBar);

      return {
        element: overlay,
        messageContainer: messageContainer,
        icon: icon,
        text: text,
        progressBar: progressBar
      };
    }

    function updateProgress(uploadContainer, progress) {
      var tracker = activeUploads.get(uploadContainer);
      if (!tracker) return;

      tracker.pendingProgress = progress;

      if (tracker.rafId !== null) return;

      tracker.rafId = requestAnimationFrame(function() {
        tracker.rafId = null;
        var prog = tracker.pendingProgress;
        var MESSAGES = getMessages();

        var messageIndex = getMessageIndex(prog, MESSAGES);
        if (messageIndex !== tracker.currentMessageIndex) {
          updateMessage(tracker, messageIndex, MESSAGES);
        }

        tracker.overlay.progressBar.style.width = prog + '%';
      });
    }

    function getMessageIndex(progress, MESSAGES) {
      for (var i = MESSAGES.length - 1; i >= 0; i--) {
        if (progress >= MESSAGES[i].threshold) return i;
      }
      return 0;
    }

    function updateMessage(tracker, messageIndex, MESSAGES) {
      tracker.currentMessageIndex = messageIndex;
      var messageContainer = tracker.overlay.messageContainer;
      var icon = tracker.overlay.icon;
      var text = tracker.overlay.text;
      var message = MESSAGES[messageIndex];

      messageContainer.style.opacity = '0';
      messageContainer.style.transform = 'scale(0.9)';
      messageContainer.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';

      setTimeout(function() {
        icon.textContent = message.icon;
        text.textContent = message.text;

        icon.style.animation = 'none';
        text.style.animation = 'none';

        requestAnimationFrame(function() {
          messageContainer.style.opacity = '1';
          messageContainer.style.transform = 'scale(1)';
          messageContainer.style.transition = 'opacity 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)';

          icon.style.animation = 'slideInLeft 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
          text.style.animation = 'slideInRight 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s';
        });
      }, MESSAGE_TRANSITION);
    }

    function cleanup(uploadContainer) {
      var tracker = activeUploads.get(uploadContainer);
      if (!tracker) return;

      if (tracker.rafId !== null) {
        cancelAnimationFrame(tracker.rafId);
      }

      if (tracker.observer) {
        tracker.observer.disconnect();
      }

      if (tracker.overlay.element.parentNode) {
        tracker.overlay.element.remove();
      }

      var originalProgress = uploadContainer.querySelector('.tee-progress');
      if (originalProgress) {
        originalProgress.style.visibility = '';
      }

      uploadContainer.removeAttribute('data-progress-monitored');
      activeUploads.delete(uploadContainer);

      log('Cleanup complete');
    }

    var checkTimeout = null;
    function checkForUploadContainers() {
      if (checkTimeout) return;

      checkTimeout = setTimeout(function() {
        checkTimeout = null;
        var containers = document.querySelectorAll('.tee-photo-uploading:not([data-progress-monitored])');

        containers.forEach(function(container) {
          container.setAttribute('data-progress-monitored', 'true');
          handleUploadProgress(container);
        });
      }, CHECK_DEBOUNCE);
    }

    var eventOptions = { passive: true };

    document.addEventListener('teeinblue-event-page-changed', function(e) {
      log('Page changed', e.detail);
      checkForUploadContainers();
    }, eventOptions);

    document.addEventListener('teeinblue-event-component-injected', function(e) {
      log('Component injected', e.detail);
      checkForUploadContainers();
    }, eventOptions);

    var domObserver = new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.addedNodes.length) {
          for (var j = 0; j < mutation.addedNodes.length; j++) {
            var node = mutation.addedNodes[j];
            if (node.nodeType === 1 &&
                (node.classList && node.classList.contains('tee-photo-uploading') ||
                 node.querySelector && node.querySelector('.tee-photo-uploading'))) {
              log('Upload container detected via DOM observer');
              checkForUploadContainers();
              return;
            }
          }
        }
      }
    });

    domObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    log('Upload monitor initialized');
  }
})();
